import {
  useCallback,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { ATTACHMENT_ACCEPT, LIMITS } from '@chatapp-inbox/shared';
import { COMPOSER_TARGET, FOCUS_TARGET_ATTRIBUTE } from '../conversation/focus-targets';
import { fillVariables, insertAtSelection, type QuickReplyItem } from '../quick-replies';
import { KIND_LABEL, formatBytes, type PendingFile } from './attachments';
import { QuickRepliesPanel, type QuickRepliesState } from './QuickRepliesPanel';
import styles from './Composer.module.css';

export interface ComposerHandle {
  focus: () => void;
}

/** Respostas rápidas no campo de mensagem (docs/quick-replies.md). Sem isto, não há botão nem atalho `/`. */
export interface ComposerQuickReplies {
  items: readonly QuickReplyItem[];
  state: QuickRepliesState;
  /** Administrador: o painel vazio diz onde cadastrar. */
  canManage: boolean;
  /** Para `{nome}` (o cliente) e `{atendente}` (quem responde). */
  clientName: string | null;
  agentName: string | null;
  onRetry: () => void;
  /** O painel abriu: bom momento para atualizar a lista. */
  onOpen: () => void;
}

/** Gravação de nota de voz (mensagem de voz do WhatsApp). Sem isto, não há botão de microfone. */
export interface ComposerVoice {
  status: 'idle' | 'asking' | 'recording';
  seconds: number;
  error: string | null;
  onStart: () => void;
  onSend: () => void;
  onCancel: () => void;
  onDismissError: () => void;
}

/** 83 → "1:23" */
export const clock = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

export interface ComposerProps {
  ref?: Ref<ComposerHandle>;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /** Sem poder enviar agora (ex.: o histórico ainda não carregou): campo e botão desabilitados. */
  disabled?: boolean;
  /** Linha discreta acima do campo (a contagem da janela de resposta). */
  hint?: ReactNode;
  /** Arquivos escolhidos, ainda não enviados. Com eles, o texto vira a legenda (limite menor). */
  files?: readonly PendingFile[];
  /** Arquivos escolhidos pelo botão ou colados da área de transferência. Sem isto, não há anexos. */
  onAttach?: (files: File[]) => void;
  onRemoveFile?: (id: string) => void;
  /** Motivos de arquivos recusados (tipo, tamanho…), mostrados até a pessoa dispensá-los. */
  fileErrors?: readonly string[];
  onDismissFileErrors?: () => void;
  quickReplies?: ComposerQuickReplies;
  /** Abre o envio de um modelo de mensagem aprovado nesta mesma conversa. Sem isto, não há botão. */
  onSendTemplate?: () => void;
  /**
   * Sussurro (mensagem que só a equipe vê, nunca vai ao cliente). Sem isto, não há botão. Ligado, o campo muda de cor e
   * de texto para ninguém mandar uma observação interna achando que é ao cliente (ou o contrário); ele fica ligado até
   * a pessoa desligá-lo.
   */
  whisper?: { active: boolean; busy: boolean; onToggle: () => void };
  voice?: ComposerVoice;
}

/** A partir de quando o contador "n/4096" aparece (fração do limite). */
const COUNTER_FROM = 0.8;

export const limitExceededText = (limit: number, caption = false): string =>
  `${caption ? 'A legenda' : 'A mensagem'} passa do limite de ${limit} caracteres. Reduza o texto para enviar.`;
export const nearLimitText = (limit: number): string => `Perto do limite de ${limit} caracteres.`;
export const LIMIT_EXCEEDED_TEXT = limitExceededText(LIMITS.messageText);
export const NEAR_LIMIT_TEXT = nearLimitText(LIMITS.messageText);

/** O que vai ao servidor: o texto aparado nas pontas. O limite vale para ele, como no servidor. */
export const outgoingText = (value: string): string => value.trim();

export type ComposerPhase = 'empty' | 'ok' | 'near' | 'over';

/** `limit` é o da mensagem (padrão) ou o da legenda, quando há arquivos para enviar. */
export function composerPhase(value: string, limit: number = LIMITS.messageText): ComposerPhase {
  const length = outgoingText(value).length;
  if (length === 0) return 'empty';
  if (length > limit) return 'over';
  return length >= Math.ceil(limit * COUNTER_FROM) ? 'near' : 'ok';
}

/**
 * Campo de resposta. Enter envia; Shift+Enter quebra a linha; Enter que apenas confirma uma
 * composição de IME (japonês, chinês, coreano, acentos com tecla morta) NÃO envia. Cresce até
 * ~8 linhas e depois rola. O texto e o rascunho vivem fora daqui (quem usa decide onde guardar).
 */
export function Composer({
  ref,
  value,
  onChange,
  onSubmit,
  disabled = false,
  hint,
  files = [],
  onAttach,
  onRemoveFile,
  fileErrors = [],
  onDismissFileErrors,
  quickReplies,
  onSendTemplate,
  whisper,
  voice,
}: ComposerProps): ReactElement {
  const whispering = whisper?.active === true;
  const recording = voice !== undefined && voice.status !== 'idle';
  const id = useId();
  const field = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const withFiles = files.length > 0;
  const limit = withFiles ? LIMITS.caption : LIMITS.messageText;
  const phase = composerPhase(value, limit);
  const length = outgoingText(value).length;
  // Só os arquivos já bastam para enviar; sem eles é preciso texto.
  const blocked =
    disabled ||
    phase === 'over' ||
    (phase === 'empty' && !withFiles) ||
    (whispering && whisper?.busy === true);

  const [quickOpen, setQuickOpen] = useState(false);
  // Onde o cursor deve ficar depois de inserir uma resposta (aplicado quando o texto novo chega ao campo).
  const caretAfter = useRef<number | null>(null);
  const openQuick = useCallback(() => {
    if (!quickReplies || disabled) return;
    quickReplies.onOpen();
    setQuickOpen(true);
  }, [quickReplies, disabled]);
  const closeQuick = useCallback((restoreFocus: boolean) => {
    setQuickOpen(false);
    if (restoreFocus) field.current?.focus({ preventScroll: true });
  }, []);
  const pickQuick = (item: QuickReplyItem): void => {
    if (!quickReplies) return;
    const text = fillVariables(item.body, {
      clientName: quickReplies.clientName,
      agentName: quickReplies.agentName,
    });
    const element = field.current;
    // O campo pode ter sido desabilitado ou ficado sem seleção: então a resposta entra no fim.
    const start = element?.selectionStart ?? value.length;
    const end = element?.selectionEnd ?? value.length;
    const next = insertAtSelection(value, start, end, text);
    caretAfter.current = next.caret;
    onChange(next.value);
    closeQuick(true);
  };

  useImperativeHandle(
    ref,
    () => ({ focus: () => field.current?.focus({ preventScroll: true }) }),
    [],
  );

  // Cresce com o conteúdo (o teto de ~8 linhas é o `max-height` do CSS). Antes da pintura, para a
  // altura nunca aparecer errada por um quadro.
  useLayoutEffect(() => {
    const element = field.current;
    if (!element) return;
    element.style.height = 'auto';
    const borders = element.offsetHeight - element.clientHeight;
    element.style.height = `${element.scrollHeight + borders}px`;
    if (caretAfter.current !== null) {
      element.setSelectionRange(caretAfter.current, caretAfter.current);
      caretAfter.current = null;
    }
  }, [value]);

  return (
    <form
      className={`${styles.composer} ${whispering ? styles.whispering : ''}`}
      aria-label={whispering ? 'Sussurrar (só a equipe vê)' : 'Responder'}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {hint}
      {quickOpen && quickReplies && (
        <QuickRepliesPanel
          items={quickReplies.items}
          state={quickReplies.state}
          canManage={quickReplies.canManage}
          onRetry={quickReplies.onRetry}
          onPick={pickQuick}
          onClose={closeQuick}
        />
      )}
      {/* A região existe antes do texto, para o leitor de tela anunciar o motivo da recusa. */}
      <div
        aria-live="assertive"
        aria-atomic="true"
        className={fileErrors.length > 0 ? styles.fileErrors : 'sr-only'}
      >
        {fileErrors.map((message) => (
          <p key={message} className={styles.fileError}>
            {message}
          </p>
        ))}
        {fileErrors.length > 0 && onDismissFileErrors && (
          <button type="button" className={styles.dismiss} onClick={onDismissFileErrors}>
            Entendi
          </button>
        )}
      </div>
      {voice?.error && (
        <p role="alert" className={styles.voiceError}>
          {voice.error}{' '}
          <button type="button" className={styles.dismiss} onClick={voice.onDismissError}>
            Entendi
          </button>
        </p>
      )}
      {withFiles && (
        <ul className={styles.files} aria-label="Arquivos para enviar">
          {files.map((f) => (
            <li key={f.id} className={styles.chip}>
              {f.previewUrl ? (
                <img className={styles.thumb} src={f.previewUrl} alt="" />
              ) : (
                <span className={styles.badge} aria-hidden="true">
                  {KIND_LABEL[f.kind]}
                </span>
              )}
              <span className={styles.chipName} dir="auto">
                {f.file.name}
              </span>
              <span className={styles.chipSize}>{formatBytes(f.file.size)}</span>
              <button
                type="button"
                className={styles.remove}
                aria-label={`Remover ${f.file.name}`}
                onClick={() => onRemoveFile?.(f.id)}
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <label htmlFor={`${id}-field`} className="sr-only">
        Mensagem
      </label>
      {/* Tocar no botão (ou onde ele está, desabilitado) não pode tirar o foco do campo: sem isto o
          foco pularia para o <main> e, no celular, o teclado fechava a cada envio. */}
      <div
        className={styles.row}
        role="presentation"
        onMouseDown={(event) => {
          if (event.target !== field.current) event.preventDefault();
        }}
      >
        {recording && voice ? (
          <div className={styles.recording} role="group" aria-label="Gravando nota de voz">
            <span className={styles.recDot} aria-hidden="true" />
            <span className={styles.recTime} role="timer" aria-live="off">
              {clock(voice.seconds)}
            </span>
            <span className={styles.recText}>
              {voice.status === 'asking'
                ? 'Permita o microfone no navegador…'
                : 'Gravando. Vai como mensagem de voz do WhatsApp.'}
            </span>
            <button type="button" className={styles.recCancel} onClick={voice.onCancel}>
              Cancelar
            </button>
            <button
              type="button"
              className={styles.send}
              disabled={voice.status !== 'recording'}
              onClick={voice.onSend}
            >
              Enviar áudio
            </button>
          </div>
        ) : (
          <>
            {onAttach && (
              <>
                <input
                  ref={picker}
                  type="file"
                  multiple
                  hidden
                  accept={ATTACHMENT_ACCEPT}
                  tabIndex={-1}
                  onChange={(event) => {
                    const chosen = Array.from(event.target.files ?? []);
                    // Zera para a mesma escolha poder ser repetida (o `change` só dispara se algo mudou).
                    event.target.value = '';
                    if (chosen.length > 0) onAttach(chosen);
                  }}
                />
                <button
                  type="button"
                  className={styles.attach}
                  aria-label="Anexar arquivo"
                  disabled={disabled || whispering}
                  onClick={() => picker.current?.click()}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M21 11.5l-8.6 8.6a5.5 5.5 0 0 1-7.8-7.8l9-9a3.7 3.7 0 0 1 5.2 5.2l-9 9a1.8 1.8 0 0 1-2.6-2.6l8.3-8.3" />
                  </svg>
                </button>
              </>
            )}
            {quickReplies && (
              <button
                type="button"
                className={styles.attach}
                aria-label="Respostas rápidas"
                aria-haspopup="dialog"
                aria-expanded={quickOpen}
                aria-keyshortcuts="/"
                title="Respostas rápidas (ou digite / com o campo vazio)"
                disabled={disabled}
                onClick={() => (quickOpen ? closeQuick(true) : openQuick())}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
                </svg>
              </button>
            )}
            <textarea
              ref={field}
              id={`${id}-field`}
              {...{ [FOCUS_TARGET_ATTRIBUTE]: COMPOSER_TARGET }}
              className={styles.field}
              rows={1}
              value={value}
              placeholder={
                disabled
                  ? 'Carregue a conversa para responder'
                  : whispering
                    ? 'Sussurro: só a equipe vê, não vai ao cliente'
                    : 'Digite uma mensagem'
              }
              disabled={disabled}
              enterKeyHint="send"
              aria-describedby={`${id}-count`}
              aria-invalid={phase === 'over' || undefined}
              onChange={(event) => onChange(event.target.value)}
              onPaste={(event) => {
                // Imagem copiada (captura de tela): vira anexo. Se vier junto texto (células de planilha,
                // por exemplo), o texto vale e a colagem segue o caminho normal.
                const pasted = Array.from(event.clipboardData.files);
                if (!onAttach || pasted.length === 0 || event.clipboardData.getData('text') !== '')
                  return;
                event.preventDefault();
                onAttach(pasted);
              }}
              onKeyDown={(event) => {
                // `/` com o campo vazio abre as respostas rápidas (a barra não entra no texto).
                if (
                  event.key === '/' &&
                  quickReplies &&
                  value === '' &&
                  !event.ctrlKey &&
                  !event.metaKey &&
                  !event.altKey &&
                  !event.nativeEvent.isComposing
                ) {
                  event.preventDefault();
                  openQuick();
                  return;
                }
                if (event.key !== 'Enter' || event.shiftKey) return;
                // `isComposing` cobre o Enter que fecha uma composição; o Safari entrega esse Enter
                // já com `isComposing` falso e `keyCode` 229.
                if (event.nativeEvent.isComposing || event.keyCode === 229) return;
                event.preventDefault();
                // Enter segurado repete o evento: um envio só por toque.
                if (!event.repeat) onSubmit();
              }}
            />
            {voice && !whispering && (
              <button
                type="button"
                className={styles.attach}
                aria-label="Gravar nota de voz"
                title="Gravar uma nota de voz (vai como mensagem de voz do WhatsApp)"
                disabled={disabled || withFiles}
                onClick={voice.onStart}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <rect x="9" y="3" width="6" height="11" rx="3" />
                  <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
                </svg>
              </button>
            )}
            <button type="submit" className={styles.send} disabled={blocked}>
              {whispering ? 'Enviar sussurro' : 'Enviar'}
            </button>
          </>
        )}
      </div>
      <div className={`${styles.meta} ${onSendTemplate || whisper ? styles.metaTools : ''}`}>
        {/* Fora da linha do campo: ali três botões quadrados apertariam o campo em tela de celular. */}
        {onSendTemplate && (
          <button
            type="button"
            className={styles.templateButton}
            aria-haspopup="dialog"
            title="Enviar um modelo de mensagem aprovado (vale também fora da janela de 24 horas)"
            disabled={disabled}
            onClick={onSendTemplate}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
              <path d="M14 3v5h5" />
              <path d="M9 13h6M9 17h6" />
            </svg>
            Enviar modelo de mensagem
          </button>
        )}
        {whisper && (
          <button
            type="button"
            className={`${styles.templateButton} ${styles.whisperButton}`}
            aria-pressed={whispering}
            title="Sussurro: uma mensagem que só a equipe vê (não vai ao cliente)"
            disabled={disabled || withFiles}
            onClick={whisper.onToggle}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
              <circle cx="12" cy="12" r="3" />
              {whispering && <path d="M3 3l18 18" />}
            </svg>
            {whispering ? 'Sussurro ligado: só a equipe vê' : 'Sussurro'}
          </button>
        )}
        {/* Texto fixo por fase (não muda a cada tecla): o aviso é dito uma vez, com educação. */}
        <p
          role="status"
          className={
            phase === 'over' ? styles.overText : phase === 'near' ? styles.nearText : 'sr-only'
          }
        >
          {phase === 'over'
            ? limitExceededText(limit, withFiles)
            : phase === 'near'
              ? nearLimitText(limit)
              : ''}
        </p>
        <span
          id={`${id}-count`}
          className={`${styles.count} ${phase === 'over' ? styles.countOver : ''}`}
        >
          {phase === 'near' || phase === 'over' ? `${length}/${limit}` : ''}
        </span>
      </div>
    </form>
  );
}
